import React, { useEffect, useRef, useState } from 'react';

export default function DeviceCheck({ onJoin, onCancel, standalone = false }) {
  const preview = useRef(null);
  const stream = useRef(null);
  const generation = useRef(0);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [heard, setHeard] = useState(false);
  const [level, setLevel] = useState(0);
  const audio = useRef(null);
  const frame = useRef(null);
  const stop = () => {
    generation.current += 1;
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
    cancelAnimationFrame(frame.current);
    audio.current?.close().catch(() => {});
    audio.current = null;
  };
  useEffect(() => stop, []);
  const check = async () => {
    stop();
    const attempt = generation.current;
    setStatus('checking');
    setError('');
    setHeard(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("הגישה למצלמה דורשת חיבור מאובטח ודפדפן תומך.");
      const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (attempt !== generation.current) {
        media.getTracks().forEach(track => track.stop());
        return;
      }
      stream.current = media;
      preview.current.srcObject = media;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        audio.current = new AudioContext();
        const analyser = audio.current.createAnalyser();
        analyser.fftSize = 256;
        audio.current.createMediaStreamSource(media).connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        const measure = () => {
          if (attempt !== generation.current) return;
          analyser.getByteTimeDomainData(samples);
          setLevel(Math.min(100, Math.max(...samples.map(value => Math.abs(value - 128))) * 4));
          frame.current = requestAnimationFrame(measure);
        };
        measure();
      }
      setStatus('ready');
    } catch (err) {
      if (attempt !== generation.current) return;
      stop();
      setStatus('error');
      setError(err.name === 'NotAllowedError'
        ? "אפשרו גישה למצלמה ולמיקרופון בהגדרות הדפדפן ונסו שוב."
        : err.name === 'NotFoundError' ? "חברו מצלמה ומיקרופון ונסו שוב."
        : err.message || "לא ניתן להפעיל את המצלמה והמיקרופון. סגרו אפליקציות אחרות שמשתמשות בהם ונסו שוב.");
    }
  };
  const playTone = async () => {
    try {
      if (!audio.current) throw new Error("השמעת קול אינה זמינה בדפדפן הזה.");
      await audio.current.resume();
      const tone = audio.current.createOscillator();
      const gain = audio.current.createGain();
      gain.gain.value = 0.08;
      tone.connect(gain).connect(audio.current.destination);
      tone.frequency.value = 440;
      tone.start();
      tone.stop(audio.current.currentTime + 0.4);
    } catch (err) { setError(err.message); }
  };
  return <section className="device-check" aria-labelledby="device-title">
    <h2 id="device-title">בדיקת מצלמה, מיקרופון ורמקולים</h2>
    <p>התצוגה הזאת נשארת במכשיר שלכם. לאחר ההיכרות הקולית, הצד השני יראה וידאו חי עם הפילטר שבחרתם.</p>
    <video ref={preview} autoPlay playsInline muted aria-label="תצוגת מצלמה פרטית" />
    <label>עוצמת המיקרופון <meter min="0" max="100" value={level} /></label>
    <p>אמרו כמה מילים ובדקו שמד עוצמת המיקרופון זז.</p>
    {error && <p role="alert" className="inline-error">{error}</p>}
    <div className="journey-actions">
      <button onClick={check} disabled={status === 'checking'}>{status === 'checking' ? "בודקים מצלמה ומיקרופון…" : "בדיקת מצלמה ומיקרופון"}</button>
      {status === 'ready' && <button onClick={playTone}>השמעת צליל בדיקה</button>}
    </div>
    {status === 'ready' && <label><input type="checkbox" checked={heard} onChange={event => setHeard(event.target.checked)} /> אני רואה את התצוגה, שומע/ת את הצליל והמיקרופון מגיב.</label>}
    <div className="journey-actions">
      {!standalone&&<button disabled={status !== 'ready' || !heard} onClick={() => { stop(); onJoin(); }}>תחילת חיפוש התאמה</button>}
      <button onClick={() => { stop(); onCancel(); }}>חזרה</button>
    </div>
  </section>;
}
